import React, { ChangeEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FormResponseErrorType, FormUtils, useFormContext } from '../../../molecules/Form';
import FormsRow from '../../../molecules/Form/components/FormsRow/FormsRow';
import LiveRegion from '../../../atoms/LiveRegion/LiveRegion';
import FormTextarea from '../../../molecules/Form/components/FormTextarea/FormTextarea';
import { InteractionStatus } from '../../../../types/InteractionStatus';
import useRedirect from '../../../../hooks/useRedirect';
import { Views } from '../../../organisms/BlockInteractions';
import { translatePartOfStringWithLink } from '../../../../lib/StringUtils/StringHTMLUtils';
import { useAppContext } from '../../../../contexts/AppContext/AppContext';
import { FormSubmitButton } from '../../../molecules/Form/components/FormSubmitButton/FormSubmitButton';
import { useModalContext } from '../../../organisms/ModalDialog/ModalContext';

type RequestActivityFormTemplateProps = {
  translationNamespace: string;
  refreshBlock: () => void;
};

export function RequestActivityFormTemplate({ translationNamespace, refreshBlock }: RequestActivityFormTemplateProps) {
  const { t } = useTranslation();
  const { status, inputs, onChange, getStatus, responseUrl } = useFormContext();
  const [startRedirect] = useRedirect();
  const { closeModal } = useModalContext();

  const { portalConfiguration } = useAppContext();
  const email = portalConfiguration?.son_portal_support_email
    ? `mailto:${portalConfiguration.son_portal_support_email}`
    : null;

  const getStatusMessage = () => {
    if (
      responseUrl &&
      (translationNamespace.endsWith(Views.ACCEPT_ACTIVATION) || translationNamespace.endsWith(Views.REJECT_ACTIVATION))
    ) {
      return FormUtils.createStatusMessage(t(`${translationNamespace}:status.successReadRestricted`));
    }
    return FormUtils.createStatusMessage(t(`${translationNamespace}:status.success`));
  };

  const statusMessages = {
    [InteractionStatus.SUCCESS]: getStatusMessage(),
    [FormResponseErrorType.NO_TEAMROOM_APPROVER_FOUND]: FormUtils.createStatusMessage(
      translatePartOfStringWithLink(
        `${translationNamespace}.status.${FormResponseErrorType.NO_TEAMROOM_APPROVER_FOUND}`,
        email,
      ),
    ),
  };

  useEffect(() => {
    if (status === InteractionStatus.SUCCESS) {
      if (responseUrl) {
        startRedirect(responseUrl);
      } else {
        setTimeout(() => {
          refreshBlock();
          closeModal();
        }, 3000);
      }
    }
  }, [closeModal, refreshBlock, responseUrl, startRedirect, status]);

  return (
    <>
      <FormsRow>
        <FormTextarea
          type={inputs.reason.type}
          id="invite-unregistered"
          name={inputs.reason.name}
          value={inputs.reason.value}
          labelTitle={t(`${translationNamespace}:labelTitle`)}
          required={inputs.reason.required}
          onChange={(event: ChangeEvent<HTMLTextAreaElement> | undefined) => {
            onChange(inputs.reason.name, event?.target.value);
          }}
          maxLength={inputs.reason.constraints?.maxLength}
        />
      </FormsRow>
      <FormSubmitButton buttonLabel={t(`${translationNamespace}:text`)} enabled />
      <LiveRegion invisible={false} htmlTag="div">
        {getStatus(statusMessages)}
      </LiveRegion>
    </>
  );
}
